/**
 * Projects view page component.
 *
 * @category pages
 */

import { ReactElement, ReactNode, useState } from "react";
import { t } from "@lingui/core/macro";

import { useServerFeatureFlag } from "_/data/features";
import { Project, useProject, useProjectAttempts } from "_/data/projects";

import { ProjectHeader } from "_/components/project-header";
import { AttemptsListing } from "_/components/attempts-listing";
import { TelemetryConsole } from "_/components/telemetry-console";
import { Viewer } from "_/components/viewer";
import { Switch, Redirect, Route } from "_/components/router";
import { ViewerOverlayNotice } from "_/components/modal";

import { projectRoutes, projectUrls, useAnalyzeRoute } from "_/routes";

import { ViewerOverlayContext } from "./viewer-overlay-context";

import { Uuid } from "_/types";
import { isValidUuid } from "_/utils";
import { Skeleton } from "_/components/skeleton";

import * as S from "./styled";

export const ProjectNotFoundError = (): ReactElement => (
  <ViewerOverlayNotice
    title={t`common.project-not-found`}
  >{t`common.project-not-found-description`}</ViewerOverlayNotice>
);

export const BadUrlError = (): ReactElement => (
  <ViewerOverlayNotice
    title={t`common.invalid-url`}
  >{t`common.invalid-id-description`}</ViewerOverlayNotice>
);

export const ProjectDetail = ({
  projectId,
}: {
  projectId: Uuid;
}): ReactNode => {
  const [overlayRef, setOverlayRef] = useState<HTMLDivElement | null>(null);

  // Uncomment and optionally revise if we later want to show a warning banner
  // const [isAnalyzeRoute] = useRoute(projectRoutes.wildcards.analyze);

  const projectResult = useProject(projectId);
  const project = projectResult.data;

  let content: ReactElement;
  if (projectResult.isLoading) {
    content = <Skeleton variant="block" />;
  } else if (project) {
    // TODO: if this project's domain differs from the org's domain, then switch
    // the current org to the org that this project belongs to.
    content = <ProjectDetailRoutes project={project} />;
  } else if (isValidUuid(projectId)) {
    content = <ProjectNotFoundError />;
  } else {
    content = <BadUrlError />;
  }

  return (
    <ViewerOverlayContext.Provider value={overlayRef}>
      <S.Wrapper>
        {/*
          // leaving this here in case we need to to re-enable a warning banner later
          project && isAnalyzeRoute && (
          <S.WarningBanner>
            {t`components.controls.analyze.warning-banner`}
          </S.WarningBanner>
        )*/}
        {project && (
          <ProjectHeader
            project={project}
            onRefreshData={() => void projectResult.refetch()}
          />
        )}
        <S.TabContentContainer>
          {content}
          <S.OverlayRoot ref={setOverlayRef} />
        </S.TabContentContainer>
      </S.Wrapper>
    </ViewerOverlayContext.Provider>
  );
};

const AnalyzeRoute = ({ project }: { project: Project }): ReactNode => {
  const analyzeRoute = useAnalyzeRoute();
  const attemptsResult = useProjectAttempts(project.id);
  const telemetryConsoleFlag = useServerFeatureFlag("telemetry-console");

  if (analyzeRoute?.subview !== "telemetry") {
    return <Viewer project={project} />;
  }

  if (telemetryConsoleFlag.isLoading) {
    return <Skeleton variant="block" />;
  }

  // A stale bookmark or a disabled flag both redirect back to the viewer.
  if (!telemetryConsoleFlag.data) {
    return <Redirect to={projectUrls.analyze(project.id)} replace={true} />;
  }

  if (attemptsResult.isLoading) {
    return <Skeleton variant="block" />;
  }

  const attempt = attemptsResult.data?.find(
    (a) => a.id === analyzeRoute.attemptId
  );

  // A stale bookmark can name an attempt this project does not have.
  // Redirecting without one lets the header select the latest, the same
  // recovery the viewer subview gets.
  if (!attempt) {
    return <Redirect to={projectUrls.analyze(project.id)} replace={true} />;
  }

  return <TelemetryConsole attempt={attempt} />;
};

const ProjectDetailRoutes = ({
  project,
}: {
  project: Project;
}): ReactElement => (
  <Switch>
    {/*
      Must stay ahead of `combinedViewer`, which also matches `analyze/*`:
      `Switch` renders only its first match, so reordering these silently
      renders the 3D viewer for telemetry URLs. Matching `analyze` separately
      also means Prepare and Analyze no longer share one mounted `Viewer`, so
      toggling between them recreates the WebGL context.
    */}
    <Route path={projectRoutes.analyze}>
      <AnalyzeRoute project={project} />
    </Route>
    <Route path={projectRoutes.wildcards.combinedViewer}>
      <Viewer project={project} />
    </Route>
    <Route path={projectRoutes.history}>
      <AttemptsListing project={project} />
    </Route>
    <Route path={projectRoutes.wildcards.index}>
      <Redirect
        to={projectUrls.prepare(project.id, project.revisions.at(-1)?.id)}
        replace={true}
      />
    </Route>
  </Switch>
);
